import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Product, StoreBase } from '@/types'

import { useMemo, useState } from 'react'
import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router'
import {
    ChevronRight,
    Leaf,
    PackageCheck,
    Scale,
    ShoppingBag,
    Truck,
    Zap
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Reveal } from '@/components/generals/reveal'
import { Button, buttonStyles } from '@/components/generals/button'
import { WhatsAppIcon } from '@/components/generals/brand-icons'
import { StoreLayout } from '@/components/store/store-layout'
import { ProductCard } from '@/components/store/product-card'
import { StockBadge } from '@/components/store/stock-badge'
import { ProductGallery } from '@/components/store/product-gallery'
import { QuantityStepper } from '@/components/store/quantity-stepper'
import { SectionHeading } from '@/components/store/section-heading'
import { useCart } from '@/components/store/cart-context'
import { getProductBySlug, getProducts, getStoreBase } from '@/lib/store-data'
import { applyDiscount, formatRupiah, whatsappLink } from '@/lib/format'

import {
    defaultSize,
    packageArt,
    primaryCategory,
    sortedSizes
} from '@/lib/product-utils'

interface ProductDetailProps {
    store: StoreBase
    product: Product
    related: Array<Product>
}

const ProductDetailContent: FC<ProductDetailProps> = ({ store, product, related }): ReactNode => {
    const router = useRouter()
    const cart = useCart()
    const sizes = sortedSizes(product)
    const [sizeId, setSizeId] = useState<string | null>(defaultSize(product)?.id ?? null)
    const [variant, setVariant] = useState<string | null>(product.variants[0] ?? null)
    const [quantity, setQuantity] = useState(1)
    const size = sizes.find(item => item.id === sizeId) ?? null
    const art = useMemo(() => packageArt(product, size), [product, size])
    const category = primaryCategory(product)
    const price = size?.price ?? 0
    const finalPrice = applyDiscount(price, product.discount_percent)
    const savings = price - finalPrice
    const soldOut = !size || size.stock <= 0
    const maxQuantity = size ? Math.max(1, Math.min(size.stock, 99)) : 1
    const settings = store.settings

    const askLink = whatsappLink(settings.whatsapp, `Halo ${settings.store_name}, saya mau tanya tentang ${product.name}${size ? ` ukuran ${size.label}` : ''}.`)

    const addToCart = (): boolean => {
        if (!size || soldOut) return false

        cart.add({
            productId: product.id,
            slug: product.slug,
            name: product.name,
            sizeId: size.id,
            sizeLabel: size.label,
            variant,
            price: size.price,
            discountPercent: product.discount_percent,
            maxStock: size.stock,
            packageColor: product.package_color,
            accentColor: product.accent_color,
            image: product.image
        }, quantity)

        return true
    }

    const orderNow = () => {
        if (addToCart()) router.push('/keranjang')
    }

    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description: product.tagline || product.description || undefined,
        image: product.image || undefined,
        brand: { '@type': 'Brand', name: settings.store_name },
        category: category?.name,
        offers: sizes.map(item => ({
            '@type': 'Offer',
            name: item.label,
            priceCurrency: 'IDR',
            price: applyDiscount(item.price, product.discount_percent),
            availability: item.stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'
        }))
    }

    return (
        <StoreLayout store={store}>
            <Seo
                title={product.name}
                description={product.tagline || product.description?.slice(0, 155) || undefined}
                ogImage={product.image || undefined}
            />
            <Head>
                <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData).replace(/</g, '\\u003c') }} />
            </Head>

            <div className='mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-8'>
                <nav aria-label='Breadcrumb'>
                    <ol className='flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground'>
                        <li><Link href='/' className='hover:text-brand-choco'>Beranda</Link></li>
                        <li aria-hidden='true'><ChevronRight className='size-4' /></li>
                        <li><Link href='/produk' className='hover:text-brand-choco'>Produk</Link></li>
                        {category && (
                            <>
                                <li aria-hidden='true'><ChevronRight className='size-4' /></li>
                                <li>
                                    <Link href={`/produk?kategori=${category.slug}`} className='hover:text-brand-choco'>
                                        <span data-mdlfy-cms={`categories:${category.id}:name`}>{category.name}</span>
                                    </Link>
                                </li>
                            </>
                        )}
                        <li aria-hidden='true'><ChevronRight className='size-4' /></li>
                        <li className='font-semibold text-brand-choco' aria-current='page'>
                            <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                        </li>
                    </ol>
                </nav>

                <div className='mt-8 grid gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:gap-14'>
                    <ProductGallery product={product} art={art} />

                    <div>
                        <div className='flex flex-wrap items-center gap-2'>
                            {product.discount_percent > 0 && (
                                <span className='rounded-full border-2 border-brand-choco bg-brand-red px-3 py-0.5 font-heading text-sm font-bold text-white'>
                                    Diskon {product.discount_percent}%
                                </span>
                            )}
                            {product.badge && (
                                <span className='rounded-full border-2 border-brand-choco bg-brand-yellow px-3 py-0.5 font-heading text-sm font-bold text-brand-choco'>
                                    <span data-mdlfy-cms={`products:${product.id}:badge`}>{product.badge}</span>
                                </span>
                            )}
                            {product.categories.map(item => (
                                <Link
                                    key={item.id}
                                    href={`/produk?kategori=${item.slug}`}
                                    className='rounded-full border-2 border-brand-choco/15 bg-white px-3 py-0.5 text-sm font-semibold text-brand-choco hover:border-brand-choco/50'
                                >
                                    <span data-mdlfy-cms={`categories:${item.id}:name`}>{item.name}</span>
                                </Link>
                            ))}
                        </div>

                        <h1 className='mt-4 font-heading text-4xl font-bold leading-tight text-brand-choco sm:text-5xl'>
                            <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                        </h1>

                        {product.tagline && (
                            <p className='mt-3 text-lg leading-relaxed text-muted-foreground'>
                                <span data-mdlfy-cms={`products:${product.id}:tagline`}>{product.tagline}</span>
                            </p>
                        )}

                        <div className='mt-6 flex flex-wrap items-end gap-x-4 gap-y-2 rounded-[28px] border-2 border-brand-choco/10 bg-white px-6 py-5'>
                            <p className='font-heading text-4xl font-bold text-brand-choco'>{formatRupiah(finalPrice)}</p>
                            {savings > 0 && (
                                <>
                                    <p className='pb-1 text-lg text-muted-foreground line-through'>{formatRupiah(price)}</p>
                                    <p className='pb-1.5 text-sm font-bold text-brand-green'>Hemat {formatRupiah(savings)}</p>
                                </>
                            )}
                        </div>

                        {sizes.length > 0 && (
                            <fieldset className='mt-7'>
                                <legend className='font-heading text-lg font-semibold text-brand-choco'>Pilih ukuran</legend>
                                <div className='mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-3'>
                                    {sizes.map(item => {
                                        const active = item.id === sizeId
                                        const empty = item.stock <= 0

                                        return (
                                            <button
                                                key={item.id}
                                                type='button'
                                                onClick={() => {
                                                    setSizeId(item.id)
                                                    setQuantity(1)
                                                }}
                                                className={cn(
                                                    'rounded-2xl border-2 px-4 py-3 text-left transition-all focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-orange/40',
                                                    active ? 'border-brand-choco bg-brand-yellow shadow-pop-sm' : 'border-brand-choco/15 bg-white hover:border-brand-choco/50',
                                                    empty && 'opacity-60'
                                                )}
                                                aria-pressed={active}
                                            >
                                                <span className='block font-heading font-bold text-brand-choco'>
                                                    <span data-mdlfy-cms={`product_sizes:${item.id}:label`}>{item.label}</span>
                                                </span>
                                                <span className='block text-sm text-brand-choco/70'>
                                                    {empty ? 'Stok habis' : formatRupiah(applyDiscount(item.price, product.discount_percent))}
                                                </span>
                                            </button>
                                        )
                                    })}
                                </div>
                            </fieldset>
                        )}

                        {product.variants.length > 0 && (
                            <fieldset className='mt-6'>
                                <legend className='font-heading text-lg font-semibold text-brand-choco'>Pilih varian</legend>
                                <div className='mt-3 flex flex-wrap gap-2'>
                                    {product.variants.map(item => (
                                        <button
                                            key={item}
                                            type='button'
                                            onClick={() => setVariant(item)}
                                            className={cn(
                                                'rounded-full border-2 px-4 py-2 font-heading text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-orange/40',
                                                variant === item ? 'border-brand-choco bg-brand-choco text-brand-cream' : 'border-brand-choco/15 bg-white text-brand-choco hover:border-brand-choco/50'
                                            )}
                                            aria-pressed={variant === item}
                                        >
                                            {item}
                                        </button>
                                    ))}
                                </div>
                            </fieldset>
                        )}

                        <div className='mt-7 flex flex-wrap items-center gap-4'>
                            <div>
                                <p className='mb-2 font-heading text-lg font-semibold text-brand-choco'>Jumlah</p>
                                <QuantityStepper value={quantity} max={maxQuantity} onChange={setQuantity} />
                            </div>
                            <div className='pt-8'>
                                <StockBadge stock={size?.stock ?? 0} showCount />
                            </div>
                        </div>

                        <div className='mt-7 grid gap-3 sm:grid-cols-2'>
                            <Button size='lg' onClick={addToCart} disabled={soldOut}>
                                <ShoppingBag className='size-5' aria-hidden='true' />
                                Tambah ke Keranjang
                            </Button>
                            <Button size='lg' variant='dark' onClick={orderNow} disabled={soldOut}>
                                <Zap className='size-5' aria-hidden='true' />
                                Pesan Sekarang
                            </Button>
                        </div>

                        {askLink && (
                            <a href={askLink} target='_blank' rel='noopener noreferrer' className={buttonStyles({ variant: 'outline', className: 'mt-3 w-full' })}>
                                <WhatsAppIcon className='size-4 text-brand-green' />
                                Tanya dulu lewat WhatsApp
                            </a>
                        )}

                        <dl className='mt-8 grid gap-3 sm:grid-cols-2'>
                            <div className='flex items-start gap-3 rounded-2xl bg-white p-4'>
                                <Scale className='mt-0.5 size-5 shrink-0 text-brand-orange' aria-hidden='true' />
                                <div>
                                    <dt className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>Berat bersih</dt>
                                    <dd className='font-heading font-semibold text-brand-choco'>
                                        {size ? <span data-mdlfy-cms={`product_sizes:${size.id}:label`}>{size.label}</span> : '-'}
                                    </dd>
                                </div>
                            </div>
                            <div className='flex items-start gap-3 rounded-2xl bg-white p-4'>
                                <Leaf className='mt-0.5 size-5 shrink-0 text-brand-orange' aria-hidden='true' />
                                <div>
                                    <dt className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>Rasa</dt>
                                    <dd className='font-heading font-semibold text-brand-choco'>
                                        {product.flavor ? <span data-mdlfy-cms={`products:${product.id}:flavor`}>{product.flavor}</span> : '-'}
                                    </dd>
                                </div>
                            </div>
                            <div className='flex items-start gap-3 rounded-2xl bg-white p-4 sm:col-span-2'>
                                <Truck className='mt-0.5 size-5 shrink-0 text-brand-orange' aria-hidden='true' />
                                <div>
                                    <dt className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>Pengiriman</dt>
                                    <dd className='text-sm leading-relaxed text-brand-choco'>
                                        {settings.shipping_note
                                            ? <span data-mdlfy-cms={`store_settings:${settings.id}:shipping_note`}>{settings.shipping_note}</span>
                                            : 'Ongkos kirim dikonfirmasi admin lewat WhatsApp.'}
                                    </dd>
                                </div>
                            </div>
                        </dl>
                    </div>
                </div>

                <div className='mt-16 grid gap-6 lg:grid-cols-[1.4fr_1fr]'>
                    <Reveal>
                        <section className='h-full rounded-[32px] border-2 border-brand-choco/10 bg-white p-6 sm:p-8' aria-labelledby='judul-deskripsi'>
                            <h2 id='judul-deskripsi' className='font-heading text-2xl font-bold text-brand-choco'>Deskripsi produk</h2>
                            {product.description ? (
                                <p className='mt-4 whitespace-pre-line leading-relaxed text-brand-choco/80'>
                                    <span data-mdlfy-cms={`products:${product.id}:description`}>{product.description}</span>
                                </p>
                            ) : (
                                <p className='mt-4 text-muted-foreground'>Deskripsi lengkap segera hadir.</p>
                            )}
                        </section>
                    </Reveal>
                    <Reveal delay={120}>
                        <section className='h-full rounded-[32px] border-2 border-brand-choco bg-brand-cream p-6 sm:p-8' aria-labelledby='judul-komposisi'>
                            <h2 id='judul-komposisi' className='flex items-center gap-2 font-heading text-2xl font-bold text-brand-choco'>
                                <PackageCheck className='size-6 text-brand-orange' aria-hidden='true' />
                                Komposisi
                            </h2>
                            {product.composition ? (
                                <p className='mt-4 leading-relaxed text-brand-choco/80'>
                                    <span data-mdlfy-cms={`products:${product.id}:composition`}>{product.composition}</span>
                                </p>
                            ) : (
                                <p className='mt-4 text-muted-foreground'>Informasi komposisi segera hadir.</p>
                            )}
                            <p className='mt-6 rounded-2xl bg-white px-4 py-3 text-sm leading-relaxed text-brand-choco/80'>
                                Simpan di tempat sejuk dan kering. Tutup rapat kembali setelah dibuka agar tetap renyah.
                            </p>
                        </section>
                    </Reveal>
                </div>
            </div>

            {related.length > 0 && (
                <section className='mx-auto max-w-7xl px-4 pt-20 sm:px-6 lg:px-8' aria-labelledby='judul-terkait'>
                    <SectionHeading id='judul-terkait' eyebrow='Masih lapar?' title='Camilan lain yang mungkin kamu suka' />
                    <ul className='mt-8 grid grid-cols-1 gap-5 min-[480px]:grid-cols-2 lg:grid-cols-4'>
                        {related.map(item => (
                            <li key={item.id}>
                                <ProductCard product={item} />
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </StoreLayout>
    )
}

/* Keyed by product so size, variant and gallery reset when moving between products */
const ProductDetail: FC<ProductDetailProps> = (props): ReactNode => <ProductDetailContent key={props.product.id} {...props} />

export const getServerSideProps: GetServerSideProps<ProductDetailProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''

    if (!slug) return { notFound: true }

    const [store, product] = await Promise.all([getStoreBase(), getProductBySlug(slug)])

    if (!product) return { notFound: true }

    const all = await getProducts()
    const categoryIds = new Set(product.categories.map(category => category.id))
    const related = all
        .filter(item => item.id !== product.id)
        .map(item => ({ item, score: item.categories.filter(category => categoryIds.has(category.id)).length }))
        .sort((a, b) => b.score - a.score || a.item.sort_order - b.item.sort_order)
        .slice(0, 4)
        .map(entry => entry.item)

    return { props: { store, product, related } }
}

export default ProductDetail
